import { Box, Flex, Spinner, Text, useMediaQuery, useSlotRecipe } from '@chakra-ui/react';

import { dateHelpers } from '@nocowanie/core';
import { IconsLinear, IconsNocowanie } from '@nocowanie/icons';

import { SearchEngineMobileProps } from './search-engine-mobile.props';

import { RANGE_PICKER } from '../../../consts';
import { getFlexDateLabel } from '../../../helpers';
import { usePluralisationTranslation } from '../../../hooks';
import { defaultSearchEngineTranslation } from '../../../models';
import { SearchEngineInstantReservationCheckbox } from '../../molecules';

export const SearchEngineMobile = ({
    setValue,
    getValues,
    watch,
    onInputValueUpdated,
    translationData = defaultSearchEngineTranslation,
    handleDrawerOpen,
    isDrawerPending,
    isMapSearch = false,
    ...props
}: SearchEngineMobileProps) => {
    const recipe = useSlotRecipe({
        key: 'searchEngineMobile',
    });
    const styles = recipe();
    const [isXsBreakpoint] = useMediaQuery(['(max-width: 375px)']);

    const openDrawer = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        handleDrawerOpen();
    };

    const displayedDateFormat = 'dd MMM.';
    const pluralizeTranslation = usePluralisationTranslation('accommodation', 'guestsNightsLabel');
    const guestsLabel = getValues('guests.adultsCount')
        ? pluralizeTranslation(
              getValues('guests.adultsCount') + getValues('guests.childrenCount'),
              'person',
          )
        : translationData.guestsPickerLabel;
    const locationValue = getValues('location');
    const checkInDate = getValues('dates.checkInDate');
    const checkOutDate = getValues('dates.checkOutDate');
    const flexDates = getValues('flexDates') ?? RANGE_PICKER.FLEX_DATES.DEFAULT;

    const shouldShowFlexDatesLabel = flexDates > RANGE_PICKER.FLEX_DATES.DEFAULT;
    const flexDatesLabel = shouldShowFlexDatesLabel
        ? getFlexDateLabel(flexDates, translationData, isXsBreakpoint)
        : '';

    const containerStyles = {
        ...styles.container,
        borderBottomRadius: isMapSearch ? 'md' : 0,
        py: isMapSearch ? 2.5 : 4,
        boxShadow: isMapSearch ? 'searchBar' : 'none',
    };

    return (
        <Box>
            <Flex
                css={containerStyles}
                as="button"
                onClick={openDrawer}
                data-testid={'search-engine-mobile-trigger'}
                className={'wc-search-engine__mobile-trigger'}
                {...props}
            >
                <Flex direction={'column'} fontSize={'sm'} gap={1}>
                    <Text as={'span'} textAlign={'left'} fontWeight={'semibold'}>
                        {locationValue?.length
                            ? locationValue
                            : translationData.locationPlaceholder}
                    </Text>
                    <Flex gap={3}>
                        <Flex gap={isXsBreakpoint ? 1 : 2} alignItems={'center'}>
                            <IconsLinear.Calendar {...styles.icon} mb={0.5} />
                            <Text as="span" lineHeight={1}>
                                {checkInDate
                                    ? dateHelpers.format(checkInDate, displayedDateFormat)
                                    : translationData.checkInPlaceholder}
                            </Text>
                            <IconsLinear.ArrowRight {...styles.icon} size="xs" />
                            <Text as="span" lineHeight={1}>
                                {checkOutDate
                                    ? dateHelpers.format(checkOutDate, displayedDateFormat)
                                    : translationData.checkOutPlaceholder}
                            </Text>
                            {shouldShowFlexDatesLabel && (
                                <Text as={'span'} my={-0.25} ml={isXsBreakpoint ? 1 : 0}>
                                    (<IconsNocowanie.PlusMinus scale={0.6} mx={-1} />{' '}
                                    {flexDatesLabel})
                                </Text>
                            )}
                        </Flex>
                        <Flex gap={isXsBreakpoint ? 1 : 2} alignItems={'center'}>
                            <IconsLinear.Profile2User fontSize={'md'} mb={0.5} {...styles.icon} />
                            <Text as="span" lineHeight={1}>
                                {guestsLabel}
                            </Text>
                        </Flex>
                    </Flex>
                </Flex>
                {isDrawerPending ? (
                    <Spinner {...styles.icon} size="md" />
                ) : (
                    <IconsLinear.Search fontSize={'2xl'} {...styles.icon} />
                )}
            </Flex>
            {!isMapSearch ? (
                <SearchEngineInstantReservationCheckbox
                    onInputValueUpdated={onInputValueUpdated}
                    setValue={setValue}
                    watch={watch}
                    withBackground={true}
                />
            ) : null}
        </Box>
    );
};
